import { useState } from 'react';
import { Table, Button, Modal, Form, Input, Checkbox, Space, Popconfirm, Tag, message, List, Grid, Card, Typography, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PhoneOutlined, MailOutlined, EyeOutlined } from '@ant-design/icons';
import { Contractor, ContractorContact } from '../types/contractor';
import { crmApi } from '../../../api/client';

const { Text } = Typography;

interface ContractorContactsProps {
    contractor: Contractor;
    onUpdate: () => void;
}

export const ContractorContacts = ({ contractor, onUpdate }: ContractorContactsProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [editingContact, setEditingContact] = useState<ContractorContact | null>(null);
    const [selectedContact, setSelectedContact] = useState<ContractorContact | null>(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const handleOpenModal = (contact?: ContractorContact) => {
        if (contact) {
            setEditingContact(contact);
            form.setFieldsValue(contact);
        } else {
            setEditingContact(null);
            form.resetFields();
            form.setFieldsValue({ isPrimary: false });
        }
        setIsModalOpen(true);
    };

    const handleShowDetail = (contact: ContractorContact) => {
        setSelectedContact(contact);
        setIsDetailModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            if (editingContact) {
                await crmApi.updateContractorContact(editingContact.id, values);
                message.success('Zaktualizowano kontakt');
            } else {
                await crmApi.addContractorContact(contractor.id, values);
                message.success('Dodano kontakt');
            }
            setIsModalOpen(false);
            onUpdate();
        } catch (error) {
            message.error('Błąd zapisu kontaktu');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await crmApi.deleteContractorContact(id);
            message.success('Usunięto kontakt');
            setIsDetailModalOpen(false);
            onUpdate();
        } catch (error) {
            message.error('Błąd usuwania kontaktu');
        }
    };

    const columns = [
        {
            title: 'Imię i Nazwisko',
            key: 'name',
            render: (_: any, record: ContractorContact) => (
                <a onClick={() => handleShowDetail(record)} style={{ fontWeight: 500 }}>
                    {record.firstName} {record.lastName}
                </a>
            ),
        },
        { title: 'Stanowisko', dataIndex: 'role', key: 'role' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Telefon', dataIndex: 'phone', key: 'phone' },
        {
            title: 'Główny',
            dataIndex: 'isPrimary',
            render: (val: boolean) => val ? <Tag color="blue">Tak</Tag> : 'Nie'
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()} block={isMobile}>
                    Dodaj kontakt
                </Button>
            </div>

            {isMobile ? (
                <List
                    dataSource={contractor.contacts}
                    renderItem={(item) => (
                        <Card size="small" style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong onClick={() => handleShowDetail(item)}>{item.firstName} {item.lastName}</Text>
                                <Space>
                                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(item)} />
                                </Space>
                            </div>
                        </Card>
                    )}
                />
            ) : (
                <Table
                    dataSource={contractor.contacts}
                    columns={columns}
                    rowKey="id"
                    pagination={false}
                />
            )}

            {/* Modal Szczegółów */}
            <Modal
                title="Szczegóły kontaktu"
                open={isDetailModalOpen}
                onCancel={() => setIsDetailModalOpen(false)}
                footer={[
                    <Button key="edit" icon={<EditOutlined />} onClick={() => { setIsDetailModalOpen(false); handleOpenModal(selectedContact!); }}>Edytuj</Button>,
                    <Popconfirm key="del" title="Usunąć?" onConfirm={() => selectedContact && handleDelete(selectedContact.id)}><Button danger icon={<DeleteOutlined />}>Usuń</Button></Popconfirm>
                ]}
                width={500}
            >
                {selectedContact && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="Imię">{selectedContact.firstName}</Descriptions.Item>
                        <Descriptions.Item label="Nazwisko">{selectedContact.lastName}</Descriptions.Item>
                        <Descriptions.Item label="Stanowisko">{selectedContact.role || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Email"><a href={`mailto:${selectedContact.email}`}>{selectedContact.email || '-'}</a></Descriptions.Item>
                        <Descriptions.Item label="Telefon"><a href={`tel:${selectedContact.phone}`}>{selectedContact.phone || '-'}</a></Descriptions.Item>
                        <Descriptions.Item label="Główny">{selectedContact.isPrimary ? 'Tak' : 'Nie'}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Modal Formularza */}
            <Modal
                title={editingContact ? 'Edytuj kontakt' : 'Nowy kontakt'}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={() => form.submit()}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="firstName" label="Imię" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="lastName" label="Nazwisko" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="role" label="Stanowisko"><Input /></Form.Item>
                    <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}><Input /></Form.Item>
                    <Form.Item name="phone" label="Telefon"><Input /></Form.Item>
                    <Form.Item name="isPrimary" valuePropName="checked"><Checkbox>Główna osoba kontaktowa</Checkbox></Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
